import { createTemplate, HAS_SHADY_CSS, _HTMLElement } from '../utils';

const IS_IE11 = !!window.MSInputMethodContext && !!document.documentMode;

/**
 * Applies workarounds and shimming necessary to make ShadowDOM work across browsers that don't support it (see `browserslist` for list of supported browsers).
 *
 * Each element instance has a ShadowDOM attached and receives a `_styles` private property with shimmed, ready-to-mount styles.
 *
 * @category mixin
 * @param {String} styles CSS string to shim for ShadowDOM use.
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 *
 * @example
```js
import { withShadowDOM } from '@updater-legacy/components/custom-element';
import styles from './index.css';

class MyCustomElement extends withShadowDOM(styles)() {
  constructor() {
    super();
    this.shadowRoot.appendChild(this._styles);
  }
}
 ```
 */
export const withShadowDOM = (styles) => (Base = _HTMLElement) =>
  class extends Base {
    constructor() {
      super();

      this.attachShadow({ mode: 'open' });

      if (!this.constructor._styles) {
        this.constructor._styles = shimStyles(this.localName, styles);
      }

      this._styles = this.constructor._styles.content.cloneNode(true);
    }

    connectedCallback() {
      super.connectedCallback && super.connectedCallback();

      if (HAS_SHADY_CSS) {
        // ShadyCSS prescribes making this call here. We're not entirely sure
        // why or what it does. https://github.com/webcomponents/shadycss#usage
        window.ShadyCSS.styleElement(this);
      }
    }
  };

/**
 * Utility function that handles ShadyCSS styling quirks.
 * While we're polyfilling, any CSS used with ShadowDOM need to pass through
 * this function.
 *
 * @param {String} tagName The custom element's tag name.
 * @param {String} styles CSS to be scoped to the ShadowDOM.
 * @returns {HTMLTemplateElement} Either a template element that contains
 * the styles (no shimming, native ShadowDOM) or an empty template with
 * the styles extracted to the document head (shimming, no native ShadowDOM).
 *
 * @ignore (Hide from generated docs)
 */
export function shimStyles(tagName, styles) {
  if (IS_IE11) {
    // For some reason, `ShadyCSS.prepareTemplate` doesn't work in IE11 when
    // the styles include CSS variables, even w/ `shimcssproperties: false`.
    // As a workaround/trade-off, we simply strip CSS variables from IE11 styles.
    styles = styles.replace(/var\(--[\w-]+\)/gm, '');
  }

  const template = createTemplate(`<style>${styles}</style>`);

  if (HAS_SHADY_CSS) {
    // Internally, this is a no-op if the browser supports ShadowDOM.
    // If the browser doesn't support ShadowDOM, the styles are extracted
    // from the template to the document head and scoped to the component.
    window.ShadyCSS.prepareTemplate(template, tagName);
  }

  return template;
}
